import { FOETAL_HEART_OPTIONS } from "features/pregnancy/constants/antenatalDropdownOptions";

/**
 * Why this exists: `heart_heard_with_doppler` is an unconstrained free-text column, and three
 * vocabularies have been written into it over time. The dropdown once posted "yes"/"no", older
 * rows hold "Yes"/"No", and since KIN-2072 it posts the MNCMS spec wording "Present"/"Absent"
 * (Messaging ANC v0.18, Table 1, Foetal heart).
 *
 * A stored "yes" is no longer one of the dropdown's option values, so without this mapping the
 * <Select> falls through to its placeholder and an older visit opens as though the doppler had
 * never been recorded — the GP then re-enters it or saves the visit having silently lost it.
 *
 * Read-side only: stored rows are deliberately left as they are, so this runs wherever a stored
 * value is read back (form prefill and the read-only visit view), not on save.
 */
const CANONICAL_BY_STORED_VALUE = new Map([
  ["yes", "Present"],
  ["no", "Absent"],
  ["present", "Present"],
  ["absent", "Absent"],
]);

/**
 * Resolves a stored foetal-heart value to the spec wording the dropdown now uses. Casing and
 * surrounding whitespace are ignored, so "Yes" and "yes" both map to "Present".
 *
 * An unrecognised value is returned unchanged rather than guessed at: a clinical observation
 * nobody mapped must not be turned into "Present", which would invent a foetal heartbeat. Such a
 * value matches no option, so the Select shows its placeholder while the read-only view still
 * displays the stored string verbatim.
 */
export const normaliseFoetalHeart = (storedValue) => {
  if (typeof storedValue !== "string") return storedValue;

  const canonical = CANONICAL_BY_STORED_VALUE.get(
    storedValue.trim().toLowerCase()
  );

  return canonical ?? storedValue;
};

/**
 * Human-readable foetal heart value for read-only views. Falls back to the stored string so an
 * unrecognised value is still shown rather than silently hidden.
 */
export const getFoetalHeartLabel = (storedValue) => {
  if (storedValue == null || storedValue === "") return null;

  const canonical = normaliseFoetalHeart(storedValue);
  const option = FOETAL_HEART_OPTIONS.find(
    ({ value }) => value === canonical
  );

  return option?.label ?? canonical;
};
